/**
 * AdminCityPostsSection - Manage posts for a specific city page
 * @atomic @idempotent
 */
import { useState, useCallback, useEffect, memo } from 'react'
import { useParams, Link } from 'react-router-dom'
import {
  DocumentTextIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  BookmarkIcon,
  EyeIcon,
  EyeSlashIcon,
} from '@heroicons/react/24/outline'
import { BookmarkIcon as BookmarkSolid } from '@heroicons/react/24/solid'
import { adminAPI } from '../../services/api'
import { useAsyncOperation } from '../../hooks/useAsyncOperation'
import AdminPostForm from './AdminPostForm'
import { getSelectClasses } from '../../styles/inputStyles'
import { createLogger } from '../../utils/logger'

const logger = createLogger('AdminCityPostsSection')

const EMPTY_FORM = {
  language: 'en',
  slug: '',
  title: '',
  excerpt: '',
  content: '',
  image_url: '',
  post_type: 'post',
  is_published: false,
  is_pinned: false,
  translation_group_id: '',
}

const isPublishedPost = (p) => p.isPublished || p.is_published
const isPinnedPost = (p) => p.isPinned || p.is_pinned

const AdminCityPostsSection = memo(function AdminCityPostsSection() {
  const { cityId } = useParams()
  const { execute, loading } = useAsyncOperation()
  const [city, setCity] = useState(null)
  const [posts, setPosts] = useState([])
  const [langFilter, setLangFilter] = useState('en')
  const [showForm, setShowForm] = useState(false)
  const [editingPost, setEditingPost] = useState(null)
  const [form, setForm] = useState(EMPTY_FORM)
  const [formErrors, setFormErrors] = useState({})
  const [submitting, setSubmitting] = useState(false)

  const loadData = useCallback(async () => {
    await execute(async () => {
      const [cityRes, postsRes] = await Promise.all([
        adminAPI.getCity(cityId),
        adminAPI.listPosts({ city_page_id: cityId, language: langFilter, limit: 50 }),
      ])
      setCity(cityRes.data)
      setPosts(postsRes.data?.posts || postsRes.data?.data || [])
    })
  }, [cityId, langFilter, execute])

  useEffect(() => { loadData() }, [loadData])

  const openCreate = useCallback(() => {
    setEditingPost(null)
    setForm({ ...EMPTY_FORM, language: langFilter })
    setFormErrors({})
    setShowForm(true)
  }, [langFilter])

  const openEdit = useCallback(async (post) => {
    setEditingPost(post.id)
    setFormErrors({})
    setShowForm(true)
    // Fetch full post to get content (list endpoint omits it for performance)
    try {
      const res = await adminAPI.getPost(post.id)
      const p = res.data
      setForm({
        language: p.language || 'en',
        slug: p.slug || '',
        title: p.title || '',
        excerpt: p.excerpt || '',
        content: p.content || '',
        image_url: p.image_url || '',
        post_type: p.post_type || 'post',
        is_published: p.is_published || false,
        is_pinned: p.is_pinned || false,
        translation_group_id: p.translation_group_id || '',
      })
    } catch (e) {
      logger.error('Failed to load post for edit', e)
    }
  }, [])

  const validate = useCallback((f) => {
    const errs = {}
    if (!f.slug?.trim()) errs.slug = 'Slug is required'
    if (!f.title?.trim()) errs.title = 'Title is required'
    if (!f.content?.trim()) errs.content = 'Content is required'
    return errs
  }, [])

  const handleSubmit = useCallback(async () => {
    const errs = validate(form)
    if (Object.keys(errs).length) { setFormErrors(errs); return }

    setSubmitting(true)
    try {
      const payload = {
        ...form,
        scope_type: 'city',
        city_page_id: cityId,
        translation_group_id: form.translation_group_id || undefined,
      }
      if (editingPost) {
        await adminAPI.updatePost(editingPost, payload)
      } else {
        await adminAPI.createPost(payload)
      }
      setShowForm(false)
      loadData()
    } catch (err) {
      logger.error('Failed to save post', err)
    } finally {
      setSubmitting(false)
    }
  }, [form, editingPost, cityId, validate, loadData])

  const togglePublish = useCallback(async (post) => {
    await execute(async () => {
      if (isPublishedPost(post)) {
        await adminAPI.unpublishPost(post.id)
      } else {
        await adminAPI.publishPost(post.id)
      }
    }, {
      onSuccess: () => loadData(),
      onError: (err) => logger.error('Failed to toggle publish', err),
    })
  }, [execute, loadData])

  const togglePin = useCallback(async (post) => {
    await execute(() => adminAPI.pinPost(post.id), {
      onSuccess: () => loadData(),
      onError: (err) => logger.error('Failed to toggle pin', err),
    })
  }, [execute, loadData])

  const deletePost = useCallback(async (postId, title) => {
    if (!confirm(`Delete post "${title}"? This cannot be undone.`)) return
    await execute(() => adminAPI.deletePost(postId), {
      onSuccess: () => loadData(),
      onError: (err) => logger.error('Failed to delete post', err),
    })
  }, [execute, loadData])

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      {/* Header */}
      <div className="px-4 lg:px-6 py-4 border-b border-gray-200 flex flex-wrap gap-3 justify-between items-center">
        <div className="flex items-center gap-2">
          <DocumentTextIcon className="h-5 w-5 text-primary" />
          <h2 className="text-xl font-semibold text-gray-900">
            Posts — {city?.name || 'Loading…'}
          </h2>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={langFilter}
            onChange={(e) => setLangFilter(e.target.value)}
            className={getSelectClasses()}
          >
            {['en', 'es-MX', 'fr-CA', 'pt-BR', 'ru', 'zh-CN'].map((l) => (
              <option key={l} value={l}>{l}</option>
            ))}
          </select>
          <button onClick={openCreate} className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-white text-sm rounded-md hover:bg-primary-600">
            <PlusIcon className="h-4 w-4" />
            New Post
          </button>
          <Link to="/admin/cities" className="text-sm text-primary hover:text-primary-600">
            ← Cities
          </Link>
        </div>
      </div>

      {/* Create / Edit form */}
      {showForm && (
        <div className="px-4 lg:px-6 py-4 border-b border-gray-200 bg-gray-50">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">
            {editingPost ? 'Edit Post' : 'Create New Post'}
          </h3>
          <AdminPostForm
            form={form}
            onChange={setForm}
            errors={formErrors}
            onSubmit={handleSubmit}
            onCancel={() => setShowForm(false)}
            submitting={submitting}
          />
        </div>
      )}

      {loading && (
        <div className="flex justify-center py-12">
          <div className="animate-spin h-8 w-8 border-b-2 border-primary rounded-full" />
        </div>
      )}

      {/* Posts table */}
      {!loading && (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {posts.map((post) => (
                <PostRow
                  key={post.id}
                  post={post}
                  isPublished={isPublishedPost(post)}
                  isPinned={isPinnedPost(post)}
                  onEdit={() => openEdit(post)}
                  onTogglePublish={() => togglePublish(post)}
                  onTogglePin={() => togglePin(post)}
                  onDelete={() => deletePost(post.id, post.title)}
                />
              ))}
              {posts.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-12 text-center text-sm text-gray-400">
                    No posts yet. Create the first one above.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
})

export default AdminCityPostsSection

const PostRow = memo(function PostRow({ post, isPublished, isPinned, onEdit, onTogglePublish, onTogglePin, onDelete }) {
  return (
    <tr className="hover:bg-gray-50">
      <td className="px-4 py-3">
        <div className="font-medium text-gray-900 text-sm">{post.title}</div>
        <div className="text-xs text-gray-400">{post.slug}</div>
      </td>
      <td className="px-4 py-3 text-sm text-gray-600 capitalize">
        {post.postType || post.post_type || 'post'}
      </td>
      <td className="px-4 py-3">
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${isPublished ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
          {isPublished ? 'Published' : 'Draft'}
        </span>
      </td>
      <td className="px-4 py-3 text-right">
        <div className="flex items-center justify-end gap-1">
          <button onClick={onTogglePin} className={`p-1.5 rounded ${isPinned ? 'text-yellow-500' : 'text-gray-400 hover:text-yellow-500'}`} title={isPinned ? 'Unpin' : 'Pin'}>
            {isPinned ? <BookmarkSolid className="h-4 w-4" /> : <BookmarkIcon className="h-4 w-4" />}
          </button>
          <button onClick={onTogglePublish} className={`p-1.5 rounded ${isPublished ? 'text-green-600 hover:text-gray-500' : 'text-gray-400 hover:text-green-600'}`} title={isPublished ? 'Unpublish' : 'Publish'}>
            {isPublished ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
          </button>
          <button onClick={onEdit} className="p-1.5 text-gray-400 hover:text-blue-600 rounded" title="Edit">
            <PencilIcon className="h-4 w-4" />
          </button>
          <button onClick={onDelete} className="p-1.5 text-gray-400 hover:text-red-600 rounded" title="Delete">
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>
      </td>
    </tr>
  )
})
